import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  IconLayoutDashboard, IconArrowsExchange, IconChartBar, IconUsers,
  IconBeach, IconBolt, IconSettings, IconDatabase,
  IconSun, IconMoon, IconChevronDown, IconBuildingSkyscraper, IconLogout,
  IconMenu2, IconCheck
} from '@tabler/icons-react';

const PAGE_TITLES = {
  '/': 'Ana Sayfa', '/islemler': 'İşlemler', '/raporlar': 'Raporlar',
  '/personel': 'Personel', '/acente': 'Turizm Acentesi', '/admin': 'Admin Paneli',
  '/ayarlar': 'Ayarlar', '/yedekler': 'Yedekler', '/acente/paket-turlar': 'Paket Turlar',
};

export default function Layout({ children }) {
  const { user, firms, currentFirm, switchFirm, logout, loading } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [showFirmMenu, setShowFirmMenu] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const firmMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => { logout(); navigate('/login'); };

  useEffect(() => {
    const handleClick = (e) => {
      if (firmMenuRef.current && !firmMenuRef.current.contains(e.target)) setShowFirmMenu(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') { setShowFirmMenu(false); setSidebarOpen(false); }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  const pageTitle = PAGE_TITLES[location.pathname] || 'Muhasib';

  const NavItem = ({ to, icon: Icon, label, end: endProp }) => (
    <NavLink to={to} end={endProp} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
      <Icon size={17} className="nav-icon" strokeWidth={1.6} />
      <span>{label}</span>
    </NavLink>
  );

  return (
    <div className="app-layout">
      {sidebarOpen && <div className="sidebar-scrim" onClick={() => setSidebarOpen(false)} />}

      <aside className={`sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="sidebar-logo">
          <div className="brand">muhasib<span>.</span></div>

          <div style={{ position: 'relative' }} ref={firmMenuRef}>
            <button
              type="button"
              className="firm-tag"
              style={{ width: '100%' }}
              aria-expanded={showFirmMenu}
              aria-haspopup="listbox"
              onClick={() => setShowFirmMenu(v => !v)}
            >
              <span className="firm-dot" />
              <span>{currentFirm?.name || 'Firma seçin'}</span>
              <IconChevronDown size={13} style={{ marginLeft: 'auto', color: 'var(--fg-3)', flexShrink: 0 }} />
            </button>

            {showFirmMenu && (
              <div
                role="listbox"
                style={{
                  position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
                  background: 'var(--surface-1)', border: '1px solid var(--line-1)',
                  borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)',
                  zIndex: 300, overflow: 'hidden', maxHeight: 280, overflowY: 'auto',
                }}
              >
                {firms.map(f => {
                  const isCurrent = f.id === currentFirm?.id;
                  return (
                    <div
                      key={f.id}
                      role="option"
                      aria-selected={isCurrent}
                      onMouseDown={(e) => { e.preventDefault(); switchFirm(f); setShowFirmMenu(false); }}
                      style={{
                        padding: '10px 13px', cursor: 'pointer', fontSize: 13,
                        display: 'flex', alignItems: 'center', gap: 8,
                        borderBottom: '1px solid var(--line-2)',
                        background: isCurrent ? 'var(--brand-tint)' : 'transparent',
                        color: isCurrent ? 'var(--brand-strong)' : 'var(--fg-1)',
                        fontWeight: isCurrent ? 600 : 400,
                      }}
                    >
                      <IconBuildingSkyscraper size={15} strokeWidth={1.6} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                      {isCurrent && <IconCheck size={14} strokeWidth={2.2} style={{ marginLeft: 'auto', flexShrink: 0 }} />}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <nav className="nav-section">
          <div className="nav-label">Ana menü</div>
          <NavItem to="/" icon={IconLayoutDashboard} label="Ana Sayfa" end />
          <NavItem to="/islemler" icon={IconArrowsExchange} label="İşlemler" />
          <NavItem to="/raporlar" icon={IconChartBar} label="Raporlar" />
          <NavItem to="/personel" icon={IconUsers} label="Personel" />
        </nav>

        {currentFirm?.firm_type === 'agency' && (
          <nav className="nav-section">
            <div className="nav-label">Turizm</div>
            <NavItem to="/acente" icon={IconBeach} label="Acente" />
          </nav>
        )}

        {user?.role === 'admin' && (
          <nav className="nav-section">
            <div className="nav-label">Yönetim</div>
            <NavItem to="/admin" icon={IconBolt} label="Admin" />
            <NavItem to="/ayarlar" icon={IconSettings} label="Ayarlar" />
            <NavItem to="/yedekler" icon={IconDatabase} label="Yedekler" />
          </nav>
        )}

        <div className="sidebar-bottom">
          {!loading && (
            <div className="user-row" onClick={handleLogout} title="Çıkış yap" role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleLogout(); } }}>
              <div className="user-avatar">{user?.username?.slice(0, 2).toUpperCase()}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="user-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.username}</div>
                <div className="user-role">{user?.role === 'admin' ? 'Yönetici' : 'Kullanıcı'}</div>
              </div>
              <IconLogout size={15} strokeWidth={1.6} style={{ color: 'var(--fg-3)', flexShrink: 0 }} />
            </div>
          )}
        </div>
      </aside>

      <div className="main-area">
        <header className="topbar">
          <button
            type="button"
            onClick={() => setSidebarOpen(v => !v)}
            className="icon-btn sidebar-toggle"
            aria-label="Menüyü aç"
          >
            <IconMenu2 size={17} strokeWidth={1.7} />
          </button>

          <div className="topbar-title">{pageTitle}</div>

          <div className="topbar-right">
            <button
              type="button"
              onClick={toggleTheme}
              className="icon-btn"
              aria-label={theme === 'dark' ? 'Açık temaya geç' : 'Koyu temaya geç'}
            >
              {theme === 'dark'
                ? <IconSun size={17} strokeWidth={1.6} />
                : <IconMoon size={17} strokeWidth={1.6} />}
            </button>

            <div className="firm-chip">
              <IconBuildingSkyscraper size={14} strokeWidth={1.6} />
              <strong>{currentFirm?.name || '—'}</strong>
            </div>
          </div>
        </header>

        <main className="main-content">{children}</main>
      </div>
    </div>
  );
}
